import React, { createContext, useContext, useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Components
import Navigation from "@/components/Navigation";
import Hero3D from "@/components/Hero3D";
import ClientLogos from "@/components/ClientLogos";
import Showcase3D from "@/components/Showcase3D";
import AIAutomation from "@/components/AIAutomation";
import Services from "@/components/Services";
import ExtraServices from "@/components/ExtraServices";
import MaintenancePackages from "@/components/MaintenancePackages";
import AddOns from "@/components/AddOns";
import CTABanner from "@/components/CTABanner";
import WhyChooseUs from "@/components/WhyChooseUs";
import ResultsStats from "@/components/ResultsStats";
import About from "@/components/About";
import Pricing from "@/components/Pricing";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import PaymentStatus from "@/components/PaymentStatus";
import CookieBanner from "@/components/CookieBanner";

// Pages
import AlgemeneVoorwaarden from "@/pages/AlgemeneVoorwaarden";
import PrivacyBeleid from "@/pages/PrivacyBeleid";
import DisclaimerPage from "@/pages/Disclaimer";
import CookieBeleid from "@/pages/CookieBeleid";

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};

// Translations
const translations = {
  en: {
    nav: {
      services: "Services",
      portfolio: "Portfolio",
      pricing: "Pricing",
      blog: "Blog",
      contact: "Contact",
      getStarted: "Get Started"
    },
    hero: {
      overline: "WEB DESIGN • AI SOLUTIONS • SOFTWARE",
      title: "We Build Digital",
      titleHighlight: "Experiences That Convert",
      subtitle: "From stunning websites to AI-powered solutions and custom software. We're a team of developers and AI specialists creating technology that drives results.",
      cta: "Start Your Project",
      ctaSecondary: "View Our Work"
    },
    services: {
      overline: "WHAT WE DO",
      title: "Services That Drive Results",
      categories: [
        {
          name: "Web Development",
          items: [
            { title: "Custom Web Design", description: "Unique, branded websites tailored to your business goals." },
            { title: "E-Commerce Solutions", description: "Powerful online stores with seamless checkout." },
            { title: "UI/UX Design", description: "User-centered design that maximizes conversions." }
          ]
        },
        {
          name: "AI Solutions",
          items: [
            { title: "AI Avatar Creator", description: "Create stunning AI-generated avatars for your brand." },
            { title: "AI Automation", description: "Automate workflows and boost productivity with AI." },
            { title: "AI Chatbots", description: "Intelligent chatbots that engage your customers 24/7." }
          ]
        },
        {
          name: "Software Development",
          items: [
            { title: "Custom Software", description: "Tailored software solutions for your unique needs." },
            { title: "API Development", description: "Robust APIs that power your applications." },
            { title: "Code Solutions", description: "Expert coding for complex technical challenges." }
          ]
        }
      ]
    },
    about: {
      overline: "ABOUT US",
      title: "Meet The Team Behind Your Success",
      description: "Led by Steven Franken, EG Solutions is a collective of passionate developers, designers, and AI specialists. We combine creativity with cutting-edge technology to deliver solutions that exceed expectations.",
      stats: [
        { value: "150+", label: "Projects Delivered" },
        { value: "15+", label: "Expert Developers" },
        { value: "5+", label: "Years Experience" },
        { value: "98%", label: "Client Satisfaction" }
      ],
      team: {
        director: "Steven Franken",
        role: "Founder & Director"
      }
    },
    portfolio: {
      overline: "OUR WORK",
      title: "Projects That Speak",
      viewProject: "View Project"
    },
    pricing: {
      overline: "PRICING",
      title: "Investment That Pays Off",
      subtitle: "Transparent pricing. No hidden fees. Choose the package that fits your needs.",
      popular: "Most Popular",
      packages: [
        {
          name: "Starter",
          price: "€999",
          description: "Perfect for small businesses starting online",
          features: [
            "5-page responsive website",
            "Mobile-optimized design",
            "Contact form integration",
            "Basic SEO setup",
            "2 revision rounds",
            "1 month support"
          ]
        },
        {
          name: "Growth",
          price: "€2,499",
          description: "For businesses ready to scale their online presence",
          features: [
            "10-page responsive website",
            "Custom UI/UX design",
            "CMS integration",
            "Advanced SEO optimization",
            "Content management system",
            "Analytics integration",
            "3 revision rounds",
            "3 months support"
          ]
        },
        {
          name: "Premium",
          price: "€4,999",
          description: "Complete solution for established businesses",
          features: [
            "Unlimited pages",
            "E-commerce functionality",
            "Custom animations",
            "Multi-language support",
            "Advanced integrations",
            "Performance optimization",
            "Unlimited revisions",
            "12 months support"
          ]
        },
        {
          name: "Custom",
          price: "Let's Talk",
          description: "Enterprise solutions tailored to your needs",
          features: [
            "Fully custom development",
            "Complex integrations",
            "Dedicated project manager",
            "Priority support 24/7",
            "Scalable architecture",
            "Custom training sessions"
          ]
        }
      ],
      cta: "Get Started"
    },
    testimonials: {
      overline: "TESTIMONIALS",
      title: "What Our Clients Say"
    },
    faq: {
      overline: "FAQ",
      title: "Frequently Asked Questions",
      items: [
        {
          question: "How long does it take to build a website?",
          answer: "Typically, a Starter package takes 2-3 weeks, Growth takes 4-6 weeks, and Premium projects take 6-10 weeks. Custom projects are scoped individually."
        },
        {
          question: "Do I need to provide content?",
          answer: "We can work with your existing content or help you create new content. We offer copywriting services as an add-on for an additional fee."
        },
        {
          question: "Will my website be mobile-friendly?",
          answer: "Absolutely! All our websites are fully responsive and optimized for all devices – mobile, tablet, and desktop."
        },
        {
          question: "What about hosting and domain?",
          answer: "We can help you set up hosting and domain registration. Hosting starts at €15/month with our recommended partners."
        },
        {
          question: "Do you offer payment plans?",
          answer: "Yes! We offer flexible payment options: 50% upfront and 50% upon completion, or monthly payment plans for larger projects."
        },
        {
          question: "What happens after the website is live?",
          answer: "We provide support based on your package. You can also opt for our monthly maintenance plans starting at €99/month."
        }
      ]
    },
    blog: {
      overline: "BLOG",
      title: "Latest Insights",
      readMore: "Read More",
      readTime: "min read"
    },
    contact: {
      overline: "GET IN TOUCH",
      title: "Let's Build Something Great",
      subtitle: "Ready to transform your online presence? Fill out the form and we'll get back to you within 24 hours.",
      form: {
        name: "Your Name",
        email: "Email Address",
        company: "Company Name (optional)",
        phone: "Phone Number (optional)",
        budget: "Project Budget",
        budgetOptions: ["€999 - €2,499", "€2,500 - €4,999", "€5,000 - €9,999", "€10,000+"],
        projectType: "Project Type",
        projectOptions: ["New Website", "Redesign", "E-commerce", "Landing Page", "Other"],
        message: "Tell us about your project",
        submit: "Send Message",
        success: "Message sent successfully! We'll be in touch soon."
      }
    },
    footer: {
      description: "Creating stunning websites that convert visitors into customers.",
      services: "Services",
      company: "Company",
      newsletter: "Newsletter",
      newsletterText: "Get design tips and industry news.",
      emailPlaceholder: "Enter your email",
      subscribe: "Subscribe",
      rights: "All rights reserved."
    }
  },
  nl: {
    nav: {
      services: "Diensten",
      portfolio: "Portfolio",
      pricing: "Prijzen",
      blog: "Blog",
      contact: "Contact",
      getStarted: "Start Nu"
    },
    hero: {
      overline: "WEBDESIGN • AI OPLOSSINGEN • SOFTWARE",
      title: "Wij Bouwen Digitale",
      titleHighlight: "Ervaringen Die Converteren",
      subtitle: "Van verbluffende websites tot AI-aangedreven oplossingen en custom software. Wij zijn een team van developers en AI-specialisten die technologie creëren die resultaat levert.",
      cta: "Start Je Project",
      ctaSecondary: "Bekijk Ons Werk"
    },
    services: {
      overline: "WAT WE DOEN",
      title: "Diensten Die Resultaat Leveren",
      categories: [
        {
          name: "Web Development",
          items: [
            { title: "Custom Webdesign", description: "Unieke websites afgestemd op jouw bedrijfsdoelen." },
            { title: "E-Commerce Oplossingen", description: "Krachtige webshops met naadloze checkout." },
            { title: "UI/UX Design", description: "Gebruikersgerichte ontwerpen die converteren." }
          ]
        },
        {
          name: "AI Oplossingen",
          items: [
            { title: "AI Avatar Creator", description: "Creëer verbluffende AI-gegenereerde avatars voor je merk." },
            { title: "AI Automatisering", description: "Automatiseer workflows en verhoog productiviteit met AI." },
            { title: "AI Chatbots", description: "Intelligente chatbots die 24/7 klanten helpen." }
          ]
        },
        {
          name: "Software Ontwikkeling",
          items: [
            { title: "Custom Software", description: "Op maat gemaakte software voor jouw unieke behoeften." },
            { title: "API Ontwikkeling", description: "Robuuste APIs die je applicaties aandrijven." },
            { title: "Code Oplossingen", description: "Expert coding voor complexe technische uitdagingen." }
          ]
        }
      ]
    },
    about: {
      overline: "OVER ONS",
      title: "Het Team Achter Jouw Succes",
      description: "Onder leiding van Steven Franken is EG Solutions een collectief van gepassioneerde developers, designers en AI-specialisten. We combineren creativiteit met geavanceerde technologie om oplossingen te leveren die verwachtingen overtreffen.",
      stats: [
        { value: "150+", label: "Projecten Opgeleverd" },
        { value: "15+", label: "Expert Developers" },
        { value: "5+", label: "Jaar Ervaring" },
        { value: "98%", label: "Klanttevredenheid" }
      ],
      team: {
        director: "Steven Franken",
        role: "Oprichter & Directeur"
      }
    },
    portfolio: {
      overline: "ONS WERK",
      title: "Projecten Die Spreken",
      viewProject: "Bekijk Project"
    },
    pricing: {
      overline: "PRIJZEN",
      title: "Investering Die Rendeert",
      subtitle: "Transparante prijzen. Geen verborgen kosten. Kies het pakket dat bij jou past.",
      popular: "Meest Gekozen",
      packages: [
        {
          name: "Starter",
          price: "€999",
          description: "Perfect voor kleine bedrijven die online starten",
          features: [
            "5-pagina responsive website",
            "Mobiel-geoptimaliseerd design",
            "Contactformulier integratie",
            "Basis SEO setup",
            "2 revisierondes",
            "1 maand support"
          ]
        },
        {
          name: "Growth",
          price: "€2.499",
          description: "Voor bedrijven klaar om online te groeien",
          features: [
            "10-pagina responsive website",
            "Custom UI/UX design",
            "CMS integratie",
            "Geavanceerde SEO optimalisatie",
            "Blog setup",
            "Analytics integratie",
            "3 revisierondes",
            "3 maanden support"
          ]
        },
        {
          name: "Premium",
          price: "€4.999",
          description: "Complete oplossing voor gevestigde bedrijven",
          features: [
            "Onbeperkt pagina's",
            "E-commerce functionaliteit",
            "Custom animaties",
            "Meertalige ondersteuning",
            "Geavanceerde integraties",
            "Prestatie optimalisatie",
            "Onbeperkte revisies",
            "12 maanden support"
          ]
        },
        {
          name: "Custom",
          price: "Op Maat",
          description: "Enterprise oplossingen op maat gemaakt",
          features: [
            "Volledig custom ontwikkeling",
            "Complexe integraties",
            "Toegewijde projectmanager",
            "Prioriteit support 24/7",
            "Schaalbare architectuur",
            "Custom training sessies"
          ]
        }
      ],
      cta: "Start Nu"
    },
    testimonials: {
      overline: "TESTIMONIALS",
      title: "Wat Onze Klanten Zeggen"
    },
    faq: {
      overline: "FAQ",
      title: "Veelgestelde Vragen",
      items: [
        {
          question: "Hoe lang duurt het om een website te bouwen?",
          answer: "Meestal duurt een Starter pakket 2-3 weken, Growth duurt 4-6 weken, en Premium projecten nemen 6-10 weken. Custom projecten worden individueel ingeschat."
        },
        {
          question: "Moet ik zelf content aanleveren?",
          answer: "We kunnen werken met je bestaande content of helpen nieuwe content te creëren. We bieden copywriting services aan als add-on voor een extra vergoeding."
        },
        {
          question: "Wordt mijn website mobiel-vriendelijk?",
          answer: "Absoluut! Al onze websites zijn volledig responsive en geoptimaliseerd voor alle apparaten – mobiel, tablet en desktop."
        },
        {
          question: "Hoe zit het met hosting en domein?",
          answer: "We kunnen je helpen met hosting en domeinregistratie. Hosting begint vanaf €15/maand bij onze aanbevolen partners."
        },
        {
          question: "Bieden jullie betalingsplannen aan?",
          answer: "Ja! We bieden flexibele betalingsopties: 50% vooraf en 50% bij oplevering, of maandelijkse betalingsplannen voor grotere projecten."
        },
        {
          question: "Wat gebeurt er nadat de website live is?",
          answer: "We bieden support op basis van je pakket. Je kunt ook kiezen voor onze maandelijkse onderhoudsplannen vanaf €99/maand."
        }
      ]
    },
    blog: {
      overline: "BLOG",
      title: "Laatste Inzichten",
      readMore: "Lees Meer",
      readTime: "min lezen"
    },
    contact: {
      overline: "NEEM CONTACT OP",
      title: "Laten We Iets Geweldigs Bouwen",
      subtitle: "Klaar om je online aanwezigheid te transformeren? Vul het formulier in en we nemen binnen 24 uur contact op.",
      form: {
        name: "Je Naam",
        email: "E-mailadres",
        company: "Bedrijfsnaam (optioneel)",
        phone: "Telefoonnummer (optioneel)",
        budget: "Project Budget",
        budgetOptions: ["€999 - €2.499", "€2.500 - €4.999", "€5.000 - €9.999", "€10.000+"],
        projectType: "Project Type",
        projectOptions: ["Nieuwe Website", "Herontwerp", "E-commerce", "Landing Page", "Anders"],
        message: "Vertel ons over je project",
        submit: "Verstuur Bericht",
        success: "Bericht succesvol verzonden! We nemen snel contact op."
      }
    },
    footer: {
      description: "Creëren van verbluffende websites die bezoekers omzetten in klanten.",
      services: "Diensten",
      company: "Bedrijf",
      newsletter: "Nieuwsbrief",
      newsletterText: "Ontvang design tips en industrie nieuws.",
      emailPlaceholder: "Vul je e-mail in",
      subscribe: "Abonneren",
      rights: "Alle rechten voorbehouden."
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState("nl");

  const t = (path) => {
    const keys = path.split(".");
    let value = translations[language];
    for (const key of keys) {
      value = value?.[key];
    }
    return value || path;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === "en" ? "nl" : "en");
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const HomePage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navigation />
      <PaymentStatus />
      <Hero3D />
      <ClientLogos />
      <Showcase3D />
      <AIAutomation />
      <CTABanner variant="default" />
      <Services />
      <ExtraServices />
      <MaintenancePackages />
      <AddOns />
      <CTABanner variant="urgency" />
      <WhyChooseUs />
      <ResultsStats />
      <About />
      <Pricing />
      <Testimonials />
      <FAQ />
      <Contact />
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/algemene-voorwaarden" element={<AlgemeneVoorwaarden />} />
          <Route path="/privacy" element={<PrivacyBeleid />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/cookies" element={<CookieBeleid />} />
        </Routes>
        <CookieBanner />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
